import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { WaveParticipantRecord, WaveTodoMentionCandidate } from "@vida/wave-core";
import { WaveTodoMentionPicker } from "../blocks/WaveTodoMentionPicker";
import {
  activeTodoMentionQuery,
  fetchWhaleTeamAgents,
  listChatMentionCandidates,
  type WhaleTeamAgent,
} from "../../services/waveChatAgentMention";

type Props = {
  placeholder: string;
  participants: WaveParticipantRecord[];
  disabled?: boolean;
  onSend: (body: string) => Promise<void>;
};

export function WaveChatMentionComposer({
  placeholder,
  participants,
  disabled = false,
  onSend,
}: Props) {
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [agents, setAgents] = useState<WhaleTeamAgent[]>([]);
  const [mentionStart, setMentionStart] = useState<number | null>(null);
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionIndex, setMentionIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const pickerRef = useRef<HTMLDivElement>(null);
  const skipBlurRef = useRef(false);

  useEffect(() => {
    void fetchWhaleTeamAgents().then(setAgents);
  }, []);

  const mentionOpen = mentionStart != null;
  const [pickerRect, setPickerRect] = useState<{
    left: number;
    bottom: number;
    width: number;
  } | null>(null);

  // Il popover chat ha overflow:hidden: il picker va in portal fixed sul body
  // per non restare occluso dentro il widget.
  useEffect(() => {
    if (!mentionOpen) {
      setPickerRect(null);
      return;
    }
    // Al mount la client-config Whale può non essere ancora arrivata: ritenta qui.
    void fetchWhaleTeamAgents().then(setAgents);
    const update = () => {
      const rect = inputRef.current?.getBoundingClientRect();
      if (!rect) return;
      setPickerRect({
        left: rect.left,
        bottom: window.innerHeight - rect.top + 6,
        width: rect.width,
      });
    };
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [mentionOpen]);
  const mentionCandidates = useMemo(
    () => listChatMentionCandidates(participants, agents, mentionQuery),
    [participants, agents, mentionQuery],
  );

  const updateMentionState = useCallback((text: string, cursor: number) => {
    const active = activeTodoMentionQuery(text, cursor);
    if (active) {
      setMentionStart(active.start);
      setMentionQuery(active.query);
      setMentionIndex(0);
    } else {
      setMentionStart(null);
      setMentionQuery("");
      setMentionIndex(0);
    }
  }, []);

  const applyMention = useCallback(
    (candidate: WaveTodoMentionCandidate) => {
      const input = inputRef.current;
      const cursor = input?.selectionStart ?? draft.length;
      const before = draft.slice(0, mentionStart ?? cursor);
      const after = draft.slice(cursor);
      const insert = `@${candidate.label} `;
      const next = before + insert + after;
      setDraft(next);
      setMentionStart(null);
      setMentionQuery("");
      setMentionIndex(0);
      requestAnimationFrame(() => {
        const pos = before.length + insert.length;
        input?.setSelectionRange(pos, pos);
        input?.focus();
      });
    },
    [draft, mentionStart],
  );

  const submit = useCallback(async () => {
    const body = draft.trim();
    if (!body || busy || disabled) return;
    setBusy(true);
    try {
      await onSend(body);
      setDraft("");
      setMentionStart(null);
    } finally {
      setBusy(false);
    }
  }, [busy, disabled, draft, onSend]);

  return (
    <div className="wave-chat-composer">
      <div
        className="wave-chat-mention-wrap"
        onPointerDown={(e) => e.stopPropagation()}
      >
        <input
          ref={inputRef}
          className="wave-chat-composer-input"
          value={draft}
          disabled={disabled || busy}
          placeholder={placeholder}
          onChange={(e) => {
            setDraft(e.target.value);
            const cursor = e.target.selectionStart ?? e.target.value.length;
            updateMentionState(e.target.value, cursor);
          }}
          onKeyDown={(e) => {
            if (mentionOpen) {
              if (e.key === "ArrowDown") {
                e.preventDefault();
                setMentionIndex((i) =>
                  Math.min(i + 1, Math.max(mentionCandidates.length - 1, 0)),
                );
                return;
              }
              if (e.key === "ArrowUp") {
                e.preventDefault();
                setMentionIndex((i) => Math.max(i - 1, 0));
                return;
              }
              if (e.key === "Escape") {
                e.preventDefault();
                setMentionStart(null);
                return;
              }
              if (e.key === "Enter" && mentionCandidates.length > 0) {
                e.preventDefault();
                applyMention(mentionCandidates[mentionIndex] ?? mentionCandidates[0]!);
                return;
              }
            }
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              void submit();
            }
          }}
          onClick={(e) => {
            const target = e.currentTarget;
            updateMentionState(target.value, target.selectionStart ?? target.value.length);
          }}
          onKeyUp={(e) => {
            const target = e.currentTarget;
            updateMentionState(target.value, target.selectionStart ?? target.value.length);
          }}
          onBlur={() => {
            if (skipBlurRef.current) {
              skipBlurRef.current = false;
              return;
            }
            window.setTimeout(() => {
              const active = document.activeElement;
              if (pickerRef.current?.contains(active)) return;
              if (inputRef.current === active) return;
              setMentionStart(null);
            }, 0);
          }}
        />
        {mentionOpen &&
          !disabled &&
          pickerRect &&
          createPortal(
            <div
              ref={pickerRef}
              className="wave-chat-mention-picker"
              style={{
                left: pickerRect.left,
                bottom: pickerRect.bottom,
                width: pickerRect.width,
              }}
              onMouseDown={() => {
                skipBlurRef.current = true;
              }}
            >
              <WaveTodoMentionPicker
                candidates={mentionCandidates}
                selectedIndex={mentionIndex}
                participants={participants}
                onSelect={applyMention}
                onHover={setMentionIndex}
              />
            </div>,
            document.body,
          )}
      </div>
      <button
        type="button"
        className="wave-chat-send-btn"
        disabled={!draft.trim() || busy || disabled}
        onClick={() => void submit()}
        aria-label="Invia"
      >
        ➤
      </button>
    </div>
  );
}
